import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';
import { IAccount } from '@services/accounts.service';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { EventsDataService } from '@services/events-data.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { RulesDataService } from '@services/rules-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';


/**
 * FilterQueryParams
 *
 * Definition of Query Parameters
 */
export interface FilterQueryParams {

	MyActions?: Boolean;
	Complete?: boolean;
	Overdue?: boolean;
	Filter?: string;
	pageIndex?: number;
	pageSize?: number;

	SortColumn?: string;
	SortDirection?: SortDirection;
	
}


/**
 * Default Class
 */
@Component({
	selector: 'app-action-list',
	templateUrl: './action-list.component.html',
	styleUrls: ['./action-list.component.scss']
})
export class ActionListComponent implements OnInit, OnDestroy, IQueryParameterFiltering<FilterQueryParams> {


	// -------------------------------------------------------------------------
	// Query Parameter Filtering
	// -------------------------------------------------------------------------

	/**
	 * Query Parameter List to use for filtering
	 *
	 * Filter Query Params available to use
	 */
	public filterQueryParams: FilterQueryParams = {};

	/**
	 * Filter Query Param Types
	 *
	 * Definition of the Query Parameter data types
	 */
	public filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		MyActions: 'boolean',
		Complete: 'boolean',
		Overdue: 'boolean',

		Filter: 'string',
		pageIndex: 'number',
		pageSize: 'number',		

		SortColumn: 'string',
		SortDirection: 'string'

	};


	// -------------------------------------------------------------------------
	// Component State
	// -------------------------------------------------------------------------

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Account */
	public selectedAccount: IAccount;

	/** Account GUID */
	public accountGUID: string;

	/** Organisation UID */
	public organisationUID: string;

	/** Actions */
	public actions: Action[] = [];

	/** Filtered Actions */
	public filteredActions: Action[] = [];

	/** Action type fields */
	public fields: { text: string, value: string } = { text: "ActionType", value: "ActionID" };

	/** Action types - values correspond to value in DB table */
	public actionTypes: IActionType[] = [];

	/** Title Text */
	public titleText: string = "Outstanding Actions";

	/** Use Table */
	public useTable: boolean = true;

	/** Hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** Current time */
	public now: Date = new Date();

	/** Current User */
	public currentUser: WebAppUser;


	// -------------------------------------------------------------------------
	// Table Configuration
	// -------------------------------------------------------------------------

	/** Data Source */
	dataSourceActions: MatTableDataSource<Action>;

	/** Displayed Columns */
	displayedColumns: string[] = [
		'asset',
		'action type',
		'assigned to',
		'target completion',
		'created by',
		'date created',
		'date completed'
	];


	// -------------------------------------------------------------------------
	// Paginator / Sort
	// -------------------------------------------------------------------------

	private _paginatorActions: MatPaginator;

	@ViewChild('paginatorActions')
	set paginatorActions(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.pageSize) { paginator.pageSize = this.filterQueryParams.pageSize }
			if (this.filterQueryParams.pageIndex) { paginator.pageIndex = this.filterQueryParams.pageIndex }

			this._paginatorActions = paginator;

			if (this.dataSourceActions) {
				this.dataSourceActions.paginator = paginator;
			}
		}
		catch (error) {
			console.error('Error assigning Webhook paginator:', error);
		}
	}

	private _sortActions: MatSort;

	@ViewChild('sortActions')
	set sortActions(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterQueryParams.SortColumn) { sort.active = this.filterQueryParams.SortColumn }
			if (this.filterQueryParams.SortDirection) { sort.direction = this.filterQueryParams.SortDirection }

			this._sortActions = sort;

			if (this.dataSourceActions) {
				this.dataSourceActions.sort = sort;
			}
		}
		catch (error) {
			console.error('Error assigning Actions sort:', error);
		}
	}


	// -------------------------------------------------------------------------
	// Constructor
	// -------------------------------------------------------------------------

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public eventsDataService: EventsDataService,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) { }


	// -------------------------------------------------------------------------
	// Lifecycle
	// -------------------------------------------------------------------------

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
		]).pipe(
			takeUntil(this.finalise),

			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, currentUser, actionTypes, queryParamMap]) =>
				!!hierarchicalData && !!currentUser && !!actionTypes && !!queryParamMap)
		).subscribe({
			next: async ([hierarchicalData, currentUser, actionTypes, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					])
				);

				this.selectedAccount = selectedAccount;
				this.accountGUID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;
				this.actionTypes = actionTypes;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('actions') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				/** Default to look for only open cases */
				this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
					queryParamMap,
					this.filterQueryParamTypes
				);

				this.filterQueryParams.MyActions = this.filterQueryParams.MyActions ?? false;
				this.filterQueryParams.Complete = this.filterQueryParams.Complete ?? false;
				this.filterQueryParams.Overdue = this.filterQueryParams.Overdue ?? false;
				this.filterQueryParams.Filter = this.filterQueryParams.Filter ?? "";

				this.hierarchy = JSON.parse(hierarchicalData);

				this.currentUser = currentUser;

				await this.getActions();
				this.applyQueryParameters();

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}


	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}


	// -------------------------------------------------------------------------
	// Data Loading
	// -------------------------------------------------------------------------

	/**
	 * getActions
	 */
	async getActions() {

		try {
			const actions = await firstValueFrom(
				this.actionsDataService.getActions$(this.accountGUID, this.organisationUID)
			);

			if (actions) {
				let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				let listAssetIDs = list.map(asset => asset.AssetID);

				this.actions = actions.filter((x: Action) => listAssetIDs.includes(x.AssetID));
			}
		}
		catch (error: any) {
			this.actions = [];

			if (error.status == 404) { }
			else {
				this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getRuleEvents$", false);
			}
		}
		finally {
			this.assignResultsToDataSource();
		}

	}


	// -------------------------------------------------------------------------
	// Data Source Configuration
	// -------------------------------------------------------------------------

	/**
	 * assignResultsToDataSource
	 */
	assignResultsToDataSource() {

		if (!this.dataSourceActions) {
			this.dataSourceActions = new MatTableDataSource(this.actions);
		}
		else {
			this.dataSourceActions.data = this.actions;
		}

		this.dataSourceActions.filterPredicate = (data: Action, filter: string) => {

			const MyActions = this.filterQueryParams.MyActions === true;
			const Complete = this.filterQueryParams.Complete === true;
			const Overdue = this.filterQueryParams.Overdue === true;

			if (
				MyActions &&
				!(
					data.AssignedToUserSub === this.currentUser.Sub ||
					data.CompletedByUserID === this.currentUser.Name ||
					data.CreatedByUserSub === this.currentUser.Name
				)
			) {
				return false;
			}

			if (Complete && !data.Completed) {
				return false;
			}

			if (Overdue && !this.checkIfOverdue(data)) {
				return false;
			}

			const query = filter?.toLowerCase().trim() ?? '';

			if (!query) {
				return true;
			}

			// Combine the necessary fields for filtering
			const combinedStr = [
				convertEpochToDateString(data.Timestamp),
				data.AssetName,
				this.getActionTypeFromValue(data.ActionType),
				data.WorkOrderReference,
				data.AssignedToUserName,
				data.CompletedByUserID,
				data.CreatedByUserSub,
				data.Details
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query);
		};

		this.dataSourceActions.sortingDataAccessor = (item, property) => {

			switch (property) {
				case 'asset': return item.AssetName || '';
				case 'action type': return item.ActionType || '';
				case 'date created': return item.Timestamp || '';
				case 'target completion': return item.TargetCompletionTimestamp || '';
				case 'date completed': return item.CompletedTimestamp || '';
				default: return '';
			}

		};
	}


	// -------------------------------------------------------------------------
	// Query Parameter / Filter Handling
	// -------------------------------------------------------------------------

	/**
	 * Handles a change to a query parameter.
	 *
	 * @param changed - The name of the query parameter that has changed.
	 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		if (changed == "Filter") {
			event.cancel = true;
			const query = (event.target as HTMLInputElement).value.toLowerCase();
			this.filterQueryParams.Filter = query;
			this.dataSourceActions.filter = query;
		}
		else if (changed == "Complete") {
			this.titleText = "Completed Actions";
			// this.displayedColumns.push("date completed");
		}
		else if (changed == "Overdue") {
			this.titleText = "Outstanding Actions";
			// this.displayedColumns.pop();
		}
		else if (changed == "SortColumn"){
			this.filterQueryParams.SortColumn =event.active;
			this.filterQueryParams.SortDirection=event.direction;
		}
		else {

		}

		// if (this.paginatorActions) { this.paginatorActions.pageIndex = 0; }

		this.applyQueryParameters(undefined, false);
	}


	/**
	 * Applies the current query parameters to the component's data.
	 *
	 * @param page - The page or data set being queried.
	 * @param reQuery - Determines whether the data should be queried again.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {

		if (this.dataSourceActions) {
			const currentFilter = this.dataSourceActions.filter;

			this.dataSourceActions.filter = '';
			this.dataSourceActions.filter = currentFilter || this.filterQueryParams.Filter || ' ';
		}

		this.rebuildURL(reQuery);

	}


	/**
	 * Rebuilds the URL using the current query parameter values.
	 *
	 * @param reQuery - Determines whether the data should be queried again
	 * after the URL has been rebuilt.
	 */
	rebuildURL(reQuery: boolean): void {

		if (reQuery) {
			// this.router.navigate([], { queryParams: this.filterData, queryParamsHandling: 'merge' });
			this.router.navigate([], { queryParams: this.filterQueryParams });
		}
		else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}

	}


	// -------------------------------------------------------------------------
	// Pagination
	// -------------------------------------------------------------------------

	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorActions) {
			this.filterQueryParams.pageSize = pag.pageSize;
			this.filterQueryParams.pageIndex = pag.pageIndex;
		}

		this.applyQueryParameters("All", false);

	}


	// -------------------------------------------------------------------------
	// Display / Lookup Helpers
	// -------------------------------------------------------------------------

	/**
	 * getActionTypeFromValue
	 * @param value value
	 * @returns
	 */
	getActionTypeFromValue(value: number): string {

		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;

		if (result) {
			return result;
		}
		else {
			return "";
		}

	}


	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {

		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}


	/**
	 * convertEpochNoTime
	 * @param epoch epoch
	 */
	convertEpochNoTime(epoch: number): string {

		let arr = convertEpochToDateString(epoch).split(" ");

		arr.pop();

		let result: string = "";

		arr.forEach(item => {
			result = result.concat(item, " ");
		});

		return result;
	}


	/**
	 * checkIfOverdue
	 * @param action action
	 * @returns
	 */
	checkIfOverdue(action: Action): boolean {

		let result: boolean = false;

		if (
			action.TargetCompletionTimestamp != undefined &&
			(action.TargetCompletionTimestamp + (1 * 24 * 60 * 60 * 1000)) < this.now.getTime()
		) {
			if (!action.Completed) {
				result = true;
			}
		}

		return result;
	}


	// -------------------------------------------------------------------------
	// Navigation
	// -------------------------------------------------------------------------

	/**
	 * goToActionDetails
	 * @param action action
	 */
	goToActionDetails(action: Action) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/actions/${action.ActionID}`]);
	}


	// -------------------------------------------------------------------------
	// Hierarchy Helpers
	// -------------------------------------------------------------------------

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {

		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {

			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

}
